import React, { useState, useEffect } from "react";
import axios from "axios";
import { MyDomain } from "../../App";

export default function AdminNotificationBlock() {
  const [barbers, setBarbers] = useState([]);
  const [selectedBarbers, setSelectedBarbers] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetchBarbers();
  }, []);

  const fetchBarbers = async () => {
    try {
      const response = await axios.get(`${MyDomain}Admins/GetAlBarbers`);
      console.log("First barber keys:", Object.keys(response.data[0]));
      setBarbers(response.data);
    } catch (error) {
      console.error("Error fetching barbers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleBarber = (barber) => {
    setSelectedBarbers((prev) => {
      const exists = prev.find((b) => b.id === barber.id);
      if (exists) {
        return prev.filter((b) => b.id !== barber.id);
      }
      return [...prev, barber];
    });
  };

  const selectAll = () => {
    if (selectedBarbers.length === barbers.length) {
      setSelectedBarbers([]);
    } else {
      setSelectedBarbers([...barbers]);
    }
  };

  const sendNotification = async () => {
    if (!title || !body || selectedBarbers.length === 0) return;
    setIsSending(true);
    setStatus(null);

    try {
      const barbersList = selectedBarbers.map((b) => ({
        expoToken: b.expoToken,
        phoneNumber: b.phoneNumber,
        name: b.name,
      }));

      console.log("Sending to:", barbersList);

      await axios.post(`${MyDomain}Appointments2/SendPushNotificationAdmin`, {
        barbers: barbersList,
        title,
        body,
        link: link || null,
      });

      setStatus("success");
      setTitle("");
      setBody("");
      setLink("");
      setSelectedBarbers([]);
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      setStatus("error");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>🔔 Send Push Notification</h3>

      {/* Barber Selection */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTitle}>
            Select Barbers ({selectedBarbers.length}/{barbers.length})
          </span>
          <button style={styles.selectAllBtn} onClick={selectAll}>
            {selectedBarbers.length === barbers.length
              ? "Deselect All"
              : "Select All"}
          </button>
        </div>

        {isLoading ? (
          <p style={styles.loadingText}>Loading barbers...</p>
        ) : barbers.length === 0 ? (
          <p style={styles.loadingText}>No barbers with push tokens found</p>
        ) : (
          <div style={styles.barberList}>
            {barbers.map((barber) => {
              const isSelected = selectedBarbers.find(
                (b) => b.id === barber.id,
              );
              return (
                <div
                  key={barber.id}
                  style={{
                    ...styles.barberCard,
                    ...(isSelected ? styles.barberCardSelected : {}),
                  }}
                  onClick={() => toggleBarber(barber)}
                >
                  <div style={styles.checkbox}>{isSelected ? "✅" : "⬜"}</div>
                  <div style={styles.barberInfo}>
                    <span style={styles.barberName}>{barber.name}</span>
                    <span style={styles.barberPhone}>
                      {barber.countryNumber} {barber.phoneNumber}
                    </span>
                  </div>
                  {barber.image && (
                    <img src={barber.image} alt="" style={styles.barberImage} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Title, Body & Link */}
      <div style={styles.section}>
        <input
          style={styles.input}
          placeholder="Notification Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          style={{ ...styles.input, height: 100, resize: "vertical" }}
          placeholder="Notification Body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <input
          style={styles.input}
          placeholder="Link (optional) - e.g. https://example.com"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
      </div>

      {/* Send Button */}
      <button
        style={{
          ...styles.button,
          opacity:
            !title || !body || selectedBarbers.length === 0 || isSending
              ? 0.5
              : 1,
        }}
        onClick={sendNotification}
        disabled={!title || !body || selectedBarbers.length === 0 || isSending}
      >
        {isSending
          ? "Sending..."
          : `Send to ${selectedBarbers.length} barber${selectedBarbers.length !== 1 ? "s" : ""}`}
      </button>

      {status === "success" && (
        <p style={styles.successText}>✅ Notification sent!</p>
      )}
      {status === "error" && <p style={styles.errorText}>❌ Failed to send.</p>}
    </div>
  );
}

const styles = {
  container: {
    padding: 24,
    maxWidth: 600,
    margin: "2rem auto",
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(20px)",
    borderRadius: 16,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  heading: {
    fontSize: 20,
    fontWeight: "700",
    color: "#ffffff",
    margin: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    color: "#a78bfa",
    fontSize: 14,
    fontWeight: "600",
  },
  selectAllBtn: {
    background: "rgba(167, 139, 250, 0.1)",
    border: "1px solid rgba(167, 139, 250, 0.3)",
    borderRadius: 8,
    color: "#a78bfa",
    padding: "6px 12px",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: "500",
  },
  barberList: {
    maxHeight: 250,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  barberCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "rgba(255, 255, 255, 0.03)",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  barberCardSelected: {
    background: "rgba(167, 139, 250, 0.15)",
    borderColor: "rgba(167, 139, 250, 0.4)",
  },
  checkbox: {
    fontSize: 18,
  },
  barberInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  barberName: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "600",
  },
  barberPhone: {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 12,
  },
  barberImage: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid rgba(167, 139, 250, 0.3)",
  },
  loadingText: {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 14,
    textAlign: "center",
    padding: 20,
  },
  input: {
    padding: 12,
    fontSize: 15,
    borderRadius: 10,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    background: "rgba(255, 255, 255, 0.05)",
    color: "#ffffff",
    outline: "none",
    fontFamily: "inherit",
  },
  button: {
    padding: 14,
    fontSize: 16,
    fontWeight: "600",
    color: "#fff",
    background: "linear-gradient(135deg, #6366f1 0%, #a78bfa 100%)",
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
  },
  successText: {
    color: "#86efac",
    textAlign: "center",
    fontSize: 14,
  },
  errorText: {
    color: "#fca5a5",
    textAlign: "center",
    fontSize: 14,
  },
};
